import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import TextField from '../components/text-field';
import {
  assignDivisionHead,
  createDivision,
  deleteDivision,
  getDivision,
  listDivisionHeadCandidates,
  listDivisions,
  listManagers,
  updateDivision,
} from '../api/panel-api';
import { managersPath } from '../section-url';

function personName(item) {
  if (!item) return '';
  return `${item.last_name || ''} ${item.first_name || ''}`.trim();
}

function formatCount(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

function membersPhrase(count) {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} участник`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} участника`;
  return `${n} участников`;
}

function candidateOption(item) {
  const divisionTitle = item.division && item.division.title ? item.division.title : 'Вне дивизиона';
  return {
    value: item.manager_id,
    label: personName(item),
    note: divisionTitle,
    badge: item.is_head ? 'Руководитель дивизиона' : '',
    candidate: item,
  };
}

function managerAsCandidate(item) {
  return {
    manager_id: item.id,
    first_name: item.first_name,
    last_name: item.last_name,
    is_head: item.cs_role === 'rd',
    division: item.division || (item.division_id ? { id: item.division_id, title: '' } : null),
  };
}

async function loadCreateCandidates() {
  const items = [];
  let page = 1;
  let total = 0;
  do {
    const data = await listManagers({
      page,
      perPage: 100,
      filters: { status: 'working', cs_role: ['manager', 'rd'] },
    });
    items.push(...(data.items || []));
    total = data.total || items.length;
    page += 1;
  } while (items.length < total);
  return items.map(managerAsCandidate);
}

function SortHead({ label, field, sort, order, onSort }) {
  const active = sort === field;
  return (
    <button
      type="button"
      className={active && order === 'desc' ? 'sort is-desc' : 'sort'}
      onClick={() => onSort(field)}
    >
      {label}
      {active ? <Icon name="arrow-up" size={14} /> : null}
    </button>
  );
}

function headConfirmCopy(division, current, next) {
  const fromLast = current ? current.last_name : '';
  const title = division.title;
  if (!next) {
    return `«${title}» останется без руководителя. ${fromLast} станет менеджером вне дивизиона. Клиенты сохранятся за ней.`;
  }
  if (next.is_head && next.division && next.division.id && next.division.id !== division.id) {
    const other = next.division.title;
    if (current) {
      return `«${other}» останется без руководителя, ${fromLast} станет менеджером «${title}». Клиенты сохранятся у обеих.`;
    }
    return `«${other}» останется без руководителя. Клиенты сохранятся.`;
  }
  if (current) {
    return `${fromLast} станет менеджером «${title}». Клиенты сохранятся у обоих.`;
  }
  return '';
}

function needsHeadConfirm(division, currentId, nextId, nextCandidate) {
  if ((nextId || null) === (currentId || null)) return false;
  if (currentId) return true;
  return Boolean(nextCandidate && nextCandidate.is_head);
}

export function DivisionPropertiesDialog({
  mode,
  division,
  presetHeadId,
  presetHeadLabel,
  presetIsHead = false,
  readOnly = false,
  onClose,
  onSaved,
}) {
  const [title, setTitle] = useState(division ? division.title : '');
  const [comment, setComment] = useState((division && division.comment) || '');
  const [headId, setHeadId] = useState((division && division.head_manager_id) || presetHeadId || '');
  const [candidates, setCandidates] = useState([]);
  const [titleError, setTitleError] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const creating = mode === 'create';
  const currentHeadId = (division && division.head_manager_id) || '';
  const noneOption = { value: '', label: 'Без руководителя' };
  const extraPreset = presetHeadId && !candidates.some((item) => item.manager_id === presetHeadId)
    ? [{
      manager_id: presetHeadId,
      first_name: '',
      last_name: presetHeadLabel || '',
      is_head: presetIsHead,
      division: null,
    }]
    : [];
  const headOptions = [
    noneOption,
    ...[...extraPreset, ...candidates].map(candidateOption),
  ];

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    async function load() {
      try {
        if (creating || !division) {
          const items = await loadCreateCandidates();
          if (!cancelled) setCandidates(items);
          return;
        }
        const data = await listDivisionHeadCandidates(division.id, { perPage: 100 });
        if (!cancelled) setCandidates(data.items || []);
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [creating, division, readOnly]);

  function searchHeads(query) {
    if (creating || !division) return;
    listDivisionHeadCandidates(division.id, { q: query, perPage: 100 })
      .then((data) => setCandidates(data.items || []))
      .catch(() => {});
  }

  function selectedCandidate() {
    return candidates.find((item) => item.manager_id === headId)
      || extraPreset.find((item) => item.manager_id === headId)
      || null;
  }

  function currentHead() {
    if (!division || !division.head) return currentHeadId ? { last_name: '', first_name: '' } : null;
    return {
      last_name: division.head.last_name,
      first_name: division.head.first_name,
      manager_id: currentHeadId,
    };
  }

  async function persist() {
    setPending(true);
    setError('');
    setTitleError('');
    try {
      let saved = division;
      if (creating) {
        saved = await createDivision({
          title: title.trim(),
          comment: comment.trim() || undefined,
        });
        if (headId) await assignDivisionHead(saved.id, headId);
      } else {
        const nextTitle = title.trim();
        const nextComment = comment.trim();
        const sameTitle = nextTitle === (division.title || '');
        const sameComment = nextComment === (division.comment || '');
        if (!sameTitle || !sameComment) {
          saved = await updateDivision(division.id, {
            title: nextTitle,
            comment: nextComment || null,
          });
        }
        if ((headId || null) !== (currentHeadId || null)) {
          saved = await assignDivisionHead(division.id, headId || null);
        }
      }
      onSaved(saved);
    } catch (actionError) {
      const field = actionError.fields && (actionError.fields.title || actionError.fields.name);
      if (field) setTitleError(field);
      else setError(actionError.message);
      setConfirm(null);
    } finally {
      setPending(false);
    }
  }

  function submit() {
    if (!title.trim()) return;
    const next = selectedCandidate();
    if (needsHeadConfirm(division || { id: '', title: title.trim() }, currentHeadId, headId, next)) {
      setConfirm({ next });
      return;
    }
    persist();
  }

  if (confirm) {
    const next = confirm.next;
    const current = currentHead();
    const target = division || { title: title.trim() };
    return (
      <div className="overlay overlay--front">
        <div className="dialog" role="dialog" aria-modal="true">
          <div className="dialog__head">
            <h2>Заменить руководителя дивизиона «{target.title}»?</h2>
            <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}>
              <Icon name="x" size={18} />
            </button>
          </div>
          <div className="dialog__body">
            <div className="who">
              <span>{current ? personName(current) : 'Без руководителя'}</span>
              <Icon name="chevron-down" size={16} />
              <strong>{next ? personName(next) : 'Без руководителя'}</strong>
            </div>
            <textarea className="comment" placeholder="Комментарий" readOnly={false} defaultValue="" />
            <div className="note note--warning">
              <Icon name="alert" size={18} />
              <p>{headConfirmCopy(target, current, next)}</p>
            </div>
            {error ? <p className="empty">{error}</p> : null}
          </div>
          <div className="dialog__footer">
            <button type="button" className="btn btn--secondary btn--small" onClick={() => setConfirm(null)}>Назад</button>
            <button type="button" className="btn btn--small" disabled={pending} onClick={persist}>Заменить</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay overlay--front">
      <div className="dialog" role="dialog" aria-modal="true">
        <div className="dialog__head">
          <h2>{creating ? 'Создать дивизион' : `Дивизион «${division.title}»`}</h2>
          <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="dialog__body">
          <TextField
            label="Название"
            value={title}
            error={titleError}
            readOnly={readOnly}
            onChange={(value) => {
              setTitle(value);
              setTitleError('');
            }}
          />
          <SelectField
            label="Руководитель"
            required={false}
            value={headId}
            onChange={setHeadId}
            options={
              readOnly
                ? [{
                  value: headId,
                  label: division && division.head
                    ? `${division.head.last_name || ''} ${division.head.first_name || ''}`.trim() || 'Без руководителя'
                    : 'Без руководителя',
                }]
                : headOptions
            }
            searchable={!readOnly}
            disabled={readOnly}
            onSearch={creating || readOnly ? undefined : searchHeads}
          />
          <textarea
            className="comment"
            placeholder="Комментарий"
            value={comment}
            readOnly={readOnly}
            onChange={(event) => setComment(event.target.value)}
          />
          {error ? <p className="empty">{error}</p> : null}
        </div>
        <div className="dialog__footer">
          {readOnly ? (
            <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>Закрыть</button>
          ) : (
            <>
              <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>Отмена</button>
              <button
                type="button"
                className="btn btn--small"
                disabled={pending || !title.trim()}
                onClick={submit}
              >
                {creating ? 'Создать' : 'Сохранить'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DeleteDivisionDialog({ division, pending, error, onClose, onConfirm }) {
  const [comment, setComment] = useState('');
  const head = division.head ? personName(division.head) : 'Без руководителя';
  const lead = division.head
    ? `Руководитель ${head}, ${membersPhrase(division.managers_count)}`
    : `${head}, ${membersPhrase(division.managers_count)}`;
  return (
    <div className="overlay overlay--front">
      <div className="dialog" role="dialog" aria-modal="true">
        <div className="dialog__head">
          <h2>Удалить дивизион?</h2>
          <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="dialog__body">
          <div className="who">
            <strong>Дивизион «{division.title}»</strong>
            <span>{lead}</span>
          </div>
          <textarea
            className="comment"
            placeholder="Комментарий"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <div className="note note--danger">
            <Icon name="alert" size={18} />
            <p>
              Все участники, включая руководителя, окажутся вне дивизиона.
              Руководитель станет менеджером. Закреплённые за каждым клиенты сохранятся.
              Восстановить дивизион нельзя.
            </p>
          </div>
          {error ? <p className="empty">{error}</p> : null}
        </div>
        <div className="dialog__footer">
          <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>Отмена</button>
          <button
            type="button"
            className="btn btn--danger btn--small"
            disabled={pending}
            onClick={() => onConfirm(comment.trim())}
          >
            Удалить дивизион
          </button>
        </div>
      </div>
    </div>
  );
}

function ManagerDivisions({ sectionCode, location, canWrite, onLocation, onListChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [sort, setSort] = useState('title');
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [editor, setEditor] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [pending, setPending] = useState(false);
  const [dialogError, setDialogError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listDivisions({ sort, order })
      .then((data) => {
        if (cancelled) return;
        const rows = data.items || [];
        setItems(rows);
        setTotal(data.total || rows.length);
        setError('');
        onListChange({ items: rows, total: data.total || rows.length });
      })
      .catch((loadError) => {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sort, order]);

  useEffect(() => {
    if (!location.create && !location.divisionId) {
      setEditor(null);
      return undefined;
    }
    if (location.create) {
      if (!canWrite) {
        onLocation({ view: 'divisions' });
        return undefined;
      }
      setEditor({ mode: 'create' });
      return undefined;
    }
    let cancelled = false;
    getDivision(location.divisionId)
      .then((data) => {
        if (!cancelled) setEditor({ mode: 'edit', division: data });
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError.message);
        onLocation({ view: 'divisions' });
      });
    return () => {
      cancelled = true;
    };
  }, [location.create, location.divisionId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  function changeSort(field) {
    if (sort === field) {
      setOrder((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSort(field);
    setOrder(field === 'title' || field === 'head' ? 'asc' : 'desc');
    setPage(1);
  }

  function reload() {
    return listDivisions({ sort, order }).then((data) => {
      const rows = data.items || [];
      setItems(rows);
      setTotal(data.total || rows.length);
      onListChange({ items: rows, total: data.total || rows.length });
    });
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const visible = items.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      {error ? <div className="empty"><p>{error}</p></div> : null}
      {!error && !loading && items.length === 0 ? (
        <div className="empty managers-empty managers-empty--tab">
          <div className="empty__icon"><Icon name="people" size={24} /></div>
          <div className="empty__copy">
            <p className="empty__title">Дивизионов пока нет</p>
            <p className="empty__lead">
              Дивизион объединяет менеджеров под одним руководителем. Создайте первый — состав задаётся в карточках менеджеров.
            </p>
          </div>
          {canWrite ? (
            <a
              href={managersPath(sectionCode, { view: 'divisions', create: true })}
              className="btn btn--secondary"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                onLocation({ view: 'divisions', create: true });
              }}
            >
              Создать дивизион
            </a>
          ) : null}
        </div>
      ) : null}
      {!error && items.length ? (
        <>
          <div className="divisions-table">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 280 }}>
                    <SortHead label="Название" field="title" sort={sort} order={order} onSort={changeSort} />
                  </th>
                  <th style={{ width: 280 }}>
                    <SortHead label="Руководитель" field="head" sort={sort} order={order} onSort={changeSort} />
                  </th>
                  <th style={{ width: 140 }}>
                    <SortHead label="Менеджеры" field="managers_count" sort={sort} order={order} onSort={changeSort} />
                  </th>
                  <th style={{ width: 140 }}>
                    <SortHead label="Клиенты" field="clients_count" sort={sort} order={order} onSort={changeSort} />
                  </th>
                  <th style={{ width: 96 }} />
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr key={item.id}>
                    <td className="table__name">{item.title}</td>
                    <td>{item.head ? personName(item.head) : 'Без руководителя'}</td>
                    <td>{formatCount(item.managers_count)}</td>
                    <td>{formatCount(item.clients_count)}</td>
                    <td>
                      <div className="divisions-table__actions">
                        <button
                          type="button"
                          className="icon-button"
                          aria-label={canWrite ? 'Редактировать' : 'Свойства'}
                          onClick={() => onLocation({ view: 'divisions', divisionId: item.id })}
                        >
                          <Icon name="pencil" size={16} />
                        </button>
                        {canWrite ? (
                          <button
                            type="button"
                            className="icon-button"
                            aria-label="Удалить"
                            onClick={() => { setDialogError(''); setRemoving(item); }}
                          >
                            <Icon name="trash" size={16} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="pager">
            <label className="pager__size">
              Показывать по
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
              >
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <div className="pager__nav">
              <span>{`${from}–${to} из ${total}`}</span>
              <button type="button" className="icon-button" aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                <Icon name="chevron-left" size={16} />
              </button>
              <span className="counter counter--page">{page}</span>
              <button type="button" className="icon-button" aria-label="Следующая страница" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>
                <Icon name="chevron-right" size={16} />
              </button>
            </div>
          </footer>
        </>
      ) : null}
      {editor ? createPortal(
        <DivisionPropertiesDialog
          mode={editor.mode}
          division={editor.division}
          readOnly={!canWrite}
          onClose={() => onLocation({ view: 'divisions' })}
          onSaved={async () => {
            await reload();
            onLocation({ view: 'divisions' });
          }}
        />,
        document.body,
      ) : null}
      {removing ? createPortal(
        <DeleteDivisionDialog
          division={removing}
          pending={pending}
          error={dialogError}
          onClose={() => setRemoving(null)}
          onConfirm={async (comment) => {
            setPending(true);
            setDialogError('');
            try {
              await deleteDivision(removing.id, comment);
              setRemoving(null);
              await reload();
            } catch (actionError) {
              setDialogError(actionError.message);
            } finally {
              setPending(false);
            }
          }}
        />,
        document.body,
      ) : null}
      {toast ? createPortal(
        <div className="toast" role="status">
          <span className="toast__icon">
            <Icon name="check-circle" size={18} tone="inherit" />
          </span>
          <div className="toast__copy">
            <p className="toast__title">{toast.title}</p>
          </div>
          <button type="button" className="toast__close" aria-label="Закрыть" onClick={() => setToast(null)}>
            <Icon name="x" size={14} tone="inherit" />
          </button>
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export default ManagerDivisions;
